import { useReasonCodes } from '@/api/useMetadata/useMasterMetadata'
import { DeleteOutlined, ToolOutlined } from '@ant-design/icons'
import { GraviButton, Texto } from '@gravitate-js/excalibrr'
import { DatePicker, Flex, Form, Input, Select } from 'antd'
import type { Dayjs } from 'dayjs'
import type { RangeValueType } from 'rc-picker/lib/PickerInput/RangePicker'
import { useCallback, useMemo } from 'react'
import { TractorIcon, TrailerIcon } from '../Icons/Icons'

export type OutOfServiceFormInit = {
  dateRange: RangeValueType<Dayjs>
  reasonCode?: string
  notes?: string
  id?: string
}

type OutOfServiceFormSubmit = {
  start: Dayjs
  end: Dayjs
  reasonCode?: string
  notes?: string
}

type Equipment = {
  id: string
  label: string
  type: 'tractor' | 'trailer'
}

type OutOfServiceFormProps = {
  initialValue: OutOfServiceFormInit
  equipment: Equipment
  onSubmit: (value: OutOfServiceFormSubmit) => void
  onCancel: () => void
  onDelete?: (id: string) => void
}

const EquipmentItem = ({ label, type }: Equipment) => (
  <Flex align='center' gap={4}>
    {type === 'tractor' ? <TractorIcon /> : <TrailerIcon />}
    <Texto>{`${type === 'tractor' ? 'Tractor' : 'Trailer'}: ${label}`}</Texto>
  </Flex>
)

const ReasonCodeSelect = () => {
  const { data, isLoading } = useReasonCodes()

  const codes = useMemo(() => data?.codes?.map((x) => ({ value: x.reason, label: x.reason })), [data])

  return (
    <Form.Item rules={[{ required: true }]} required name='reasonCode' label='Reason Code'>
      <Select loading={isLoading} options={codes} optionFilterProp='label' />
    </Form.Item>
  )
}

export function OutOfServiceForm({ initialValue, equipment, onSubmit, onDelete, onCancel }: OutOfServiceFormProps) {
  const [form] = Form.useForm<OutOfServiceFormInit>()

  const onFinish = useCallback(
    (val: OutOfServiceFormInit) => {
      if (!val.dateRange[0] || !val.dateRange[1]) return
      onSubmit({
        start: val.dateRange[0],
        end: val.dateRange[1],
        reasonCode: val.reasonCode,
        notes: val.notes,
      })
    },
    [onSubmit]
  )

  return (
    <Form form={form} onFinish={onFinish} initialValues={initialValue} layout='vertical'>
      <Flex gap={4} align='center'>
        <ToolOutlined />
        <Texto category='h5'>Out of Service</Texto>
      </Flex>
      <Flex gap={4} vertical className='py-2'>
        <Texto appearance='hint'>Equipment</Texto>
        <EquipmentItem {...equipment} />
      </Flex>
      <Form.Item required name='dateRange' label='Date Range'>
        <DatePicker.RangePicker style={{ width: '100%' }} allowClear={false} />
      </Form.Item>
      <ReasonCodeSelect />
      <Form.Item name='notes' label='Notes (optional)'>
        <Input.TextArea placeholder='Enter' />
      </Form.Item>
      <Flex style={{ flexDirection: 'row-reverse', width: '100%' }} gap={6}>
        <GraviButton theme2 htmlType='submit'>
          Confirm
        </GraviButton>
        <GraviButton onClick={onCancel}>Cancel</GraviButton>
        {onDelete && initialValue.id && (
          <GraviButton
            style={{ marginRight: 'auto' }}
            error
            icon={<DeleteOutlined />}
            onClick={() => onDelete(initialValue.id!)}
          />
        )}
      </Flex>
    </Form>
  )
}
